import {
  Component,
  HostBinding,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { GlobalFilterComponent } from '@shared-lib/global-components/global-filter/global-filter.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { ToggleButtonComponent } from '@shared-lib/global-components/toggle-button/toggle-button.component';
import { ButtonComponent } from '@shared-lib/global-components/button/button.component';
import { RoleMenu, RoleMenuMap } from '../../../public/assets/user-role';

const DEFAULT_REDIRECT_PATH = '/';
const MAPPED_ROLE_STORAGE_KEY = 'dvm.user.mappedRole';
const EFFECTIVE_ROLE_STORAGE_KEY = 'dvm.user.effectiveRole';

interface RoleAccessConfig {
  defaultRole?: string;
  defaultRedirect?: string;
  roleMenu?: RoleMenuMap;
}

@Component({
  selector: 'app-dashboard',
  imports: [
    HttpClientModule,
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    ToggleButtonComponent,
    ButtonComponent,
    MatTooltipModule,
    MatIconModule,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  configurations: any = {};
  dashboardType: any;
  @ViewChildren('chartContainer', { read: ViewContainerRef })
  chartContainers!: QueryList<ViewContainerRef>;
  @ViewChild(GlobalFilterComponent) globalFilter?: GlobalFilterComponent;
  private subscriptions = new Subscription();

  globalDropdownConfig: any;
  selectedYear: number = 2024;
  configFileName!: string;
  screenFloating = true;
  chartOffsetTop = 0;
  headerInsightKey = '';

  get hasFilters(): boolean {
    return !!this.configurations?.filters;
  }

  get insightConfig(): any {
    const content: any[] = this.configurations?.filters?.content || [];
    return content.find((f: any) => f?.type === 'button' && f?.key === 'insights') ?? null;
  }

  get headerInsightConfig(): any {
    if (!this.insightConfig) return null;
    return { ...this.insightConfig, iconOnly: true };
  }

  get showHeaderInsight(): boolean {
    return this.hasFilters && !!this.insightConfig && 
           this.insightConfig?.enableInsight !== false && 
           this.insightConfig?.showInsight !== false;
  }

  get showHeaderReset(): boolean {
    return this.hasFilters;
  }

  onHeaderReset(): void {
    this.globalFilter?.resetButton();
  }

  onHeaderInsight(key: string): void {
    this.headerInsightKey = this.headerInsightKey === key ? '' : key;
    this.globalFilter?.onButtonSelectionChange(key);
  }

  @HostBinding('class.dvm-no-scroll') get noScrollClass() {
    return !this.screenFloating;
  }

  constructor(
    public configService: ConfigService,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private globalFilterService: GlobalFilterService,
    private platformService: PlatformService,
    private responsiveLayoutService: ResponsiveLayoutService
  ) { }

  ngOnInit() {
    this.responsiveLayoutService.init();
    this.enforcePageAccessAndLoadDashboard();
    this.initializeGlobalFilters();
  }

  private enforcePageAccessAndLoadDashboard(): void {
    const accessSub = this.loadRoleAccessConfig().subscribe((accessConfig) => {
      const currentPath = this.getCurrentPath();
      const effectiveRole = this.resolveEffectiveRole(accessConfig);
      const allowedPages = this.getAllowedPagesForRole(effectiveRole, accessConfig);

      if (!this.isPageAllowed(currentPath, allowedPages)) {
        const redirectPath = this.getRedirectPath(allowedPages, accessConfig);

        if (redirectPath !== currentPath) {
          void this.router.navigateByUrl(redirectPath);
        } else {
          console.warn(`Access denied for role "${effectiveRole}" on route "${currentPath}".`);
        }
        return;
      }

      this.loadDashboardConfiguration();
    });

    this.subscriptions.add(accessSub);
  }

  private loadDashboardConfiguration(): void {
    const data = this.route.snapshot.data;
    if (data['configFile']) {
      this.configFileName = data['configFile'];
      this.loadCharts();
    } else {
      const projectId = environment.PROJECT_ID;
      const appId = this.route.snapshot.data['dvmWorkflowAppId'];
      if (appId) {
        this.loadDashboardFromApi(projectId, appId);
      }
    }
  }

  private initializeGlobalFilters(): void {
    this.globalFilterService.updateAllFilters({
      queryParams: {},
      requestParams: {},
      widgetId: null,
    });
    this.globalFilterService.activeFilter = null;

    this.globalFilterService.resetFilterSelection('customer_spec');
  }

  private loadRoleAccessConfig() {
    return this.http.get<RoleAccessConfig>('assets/role-map.json').pipe(
      map((config) => this.normalizeRoleAccessConfig(config)),
      catchError(() => of(this.normalizeRoleAccessConfig({}))),
    );
  }

  private normalizeRoleAccessConfig(config: RoleAccessConfig): RoleAccessConfig {
    return {
      defaultRole: config.defaultRole,
      defaultRedirect: config.defaultRedirect ?? DEFAULT_REDIRECT_PATH,
      roleMenu: {
        ...RoleMenu,
        ...(config.roleMenu ?? {}),
      },
    };
  }

  private resolveEffectiveRole(accessConfig: RoleAccessConfig): string {
    const roleMenu = accessConfig.roleMenu ?? {};
    const knownRoles = Object.keys(roleMenu);
    const mappedRole = localStorage.getItem(MAPPED_ROLE_STORAGE_KEY)?.trim();
    const effectiveRole = mappedRole && knownRoles.includes(mappedRole) ? mappedRole : '';

    if (effectiveRole) {
      localStorage.setItem(EFFECTIVE_ROLE_STORAGE_KEY, effectiveRole);
    }

    return effectiveRole;
  }

  private getAllowedPagesForRole(role: string, accessConfig: RoleAccessConfig): string[] {
    const pages = accessConfig.roleMenu?.[role] ?? [];
    return pages.map((path) => (path === '*' ? path : this.normalizePath(path)));
  }

  private isPageAllowed(currentPath: string, allowedPages: string[]): boolean {
    if (!allowedPages.length) {
      return false;
    }

    if (allowedPages.includes('*')) {
      return true;
    }

    return allowedPages.includes(currentPath);
  }

  private getRedirectPath(allowedPages: string[], accessConfig: RoleAccessConfig): string {
    const fallbackPath = this.normalizePath(
      accessConfig.defaultRedirect ?? DEFAULT_REDIRECT_PATH,
    );

    if (allowedPages.includes('*')) {
      return fallbackPath;
    }

    const firstAllowedPath = allowedPages.find((path) => path !== '*');
    return firstAllowedPath ?? fallbackPath;
  }

  private getCurrentPath(): string {
    return this.normalizePath(this.router.url.split('?')[0]);
  }

  private normalizePath(path: string): string {
    if (!path) {
      return '/';
    }

    const [pathWithoutQuery] = path.split('?');
    if (pathWithoutQuery === '*') {
      return pathWithoutQuery;
    }

    const prefixedPath = pathWithoutQuery.startsWith('/')
      ? pathWithoutQuery
      : `/${pathWithoutQuery}`;
    const trimmedPath = prefixedPath.replace(/\/+$/, '');

    return trimmedPath || '/';
  }

  loadDashboardFromApi(projectId: string, appId: string) {
    const configSub = this.platformService
      .getDashboardConfigFromApi(projectId, appId)
      .subscribe({
        next: (response) => {
          if (response?.length) {
            this.configurations = response[0].dvm_workflow_map;
            this.applyScreenFloating(this.configurations);
            this.applyLayoutOffsets(this.configurations);
            this.responsiveLayoutService.setRowCount(this.configurations?.pageContent?.length || 0);
          }
        },
        error: (err) => console.error('Error loading dashboard config:', err),
      });

    this.subscriptions.add(configSub);
  }

  loadCharts() {
    const configSub = this.configService
      .getDashboardConfig(this.configFileName)
      .subscribe((response) => {
        this.configurations = response;
        this.applyScreenFloating(response);
        this.applyLayoutOffsets(response);
        this.responsiveLayoutService.setRowCount(response?.pageContent?.length || 0);
      });
    this.subscriptions.add(configSub);
  }

  private applyScreenFloating(config: any): void {
    // Default to true (scrolling enabled) if key is not present
    this.screenFloating = config?.screenFloating !== false;
    this.responsiveLayoutService.setScreenFloating(this.screenFloating);
  }

  private applyLayoutOffsets(config: any): void {
    const rawOffset = config?.chartOffsetTop ?? config?.layout?.chartOffsetTop ?? 0;
    const parsedOffset = Number(rawOffset);
    this.chartOffsetTop = Number.isFinite(parsedOffset) ? parsedOffset : 0;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.responsiveLayoutService.setScreenFloating(true);
    this.globalFilterService.clearAllFilters();
    this.globalFilterService.activeFilter = null;
  }
}
